import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, Crown, Fire, HeartStraight, MapPin, Martini, NavigationArrow, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl, trackAnalytics } from "../lib/api";
import { directionsUrl } from "../lib/directions";
import { useAuth } from "../context/AuthContext";
import { PulseBadge } from "./guest/Discover";
import EventChat from "../components/EventChat";

export default function VenueProfile() {
  const { user } = useAuth();
  const { id } = useParams();
  const [venue, setVenue] = useState(null);
  const [tab, setTab] = useState("tonight");
  const [favorited, setFavorited] = useState(false);
  const [goingIds, setGoingIds] = useState(new Set());
  const [onListIds, setOnListIds] = useState(new Set());
  const [paying, setPaying] = useState(false);
  const navigate = useNavigate();

  const payEntry = async () => {
    setPaying(true);
    try {
      const { data } = await API.post("/payments/entry/checkout", {
        venue_id: id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(fmtErr(e));
      setPaying(false);
    }
  };

  useEffect(() => {
    trackAnalytics("venue_view", id);
    API.get(`/venues/${id}`).then((r) => {
      setVenue(r.data);
      const going = (r.data.events || []).filter((e) => (e.going || []).includes(user?.id)).map((e) => e.id);
      if (r.data.tonight_event && (r.data.tonight_event.going || []).includes(user?.id)) going.push(r.data.tonight_event.id);
      setGoingIds(new Set(going));
    }).catch(() => toast.error("Venue not found"));
    API.get("/favorites").then((r) => {
      setFavorited(r.data.some((f) => f.type === "venue" && f.item.id === id));
    }).catch(() => {});
    API.get("/pass/tonight").then((r) => setOnListIds(new Set(r.data.map((p) => p.event_id)))).catch(() => {});
  }, [id, user?.id]);

  if (!venue) return <div className="min-h-screen bg-[#050505] px-5 pt-16 text-center text-sm text-zinc-500">Loading venue...</div>;

  const toggleFav = async () => {
    try {
      const { data } = await API.post("/favorites/toggle", { target_type: "venue", target_id: id });
      setFavorited(data.favorited);
      toast.success(data.favorited ? "Added to favorites — you'll get their drops" : "Removed from favorites");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleGoing = async (eventId) => {
    try {
      const { data } = await API.post(`/events/${eventId}/going`);
      const delta = data.going ? 1 : -1;
      setGoingIds((s) => { const n = new Set(s); data.going ? n.add(eventId) : n.delete(eventId); return n; });
      setVenue((v) => ({
        ...v,
        tonight_event: v.tonight_event?.id === eventId
          ? { ...v.tonight_event, going_count: Math.max(0, (v.tonight_event.going_count || 0) + delta) }
          : v.tonight_event,
        events: (v.events || []).map((e) => e.id === eventId
          ? { ...e, going_count: Math.max(0, (e.going_count || 0) + delta) } : e),
      }));
      toast.success(data.going ? "You're going. See you there." : "Removed from going");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const tonightEvent = venue.tonight_event;
  const events = venue.events || [];
  const music = venue.music || [];
  const images = venue.images || [];
  const videos = venue.videos || [];
  const rewardsProgram = venue.rewards_program || [];
  const promoterTeam = venue.promoter_team || [];
  const venueImage = venue.image || images[0] || "https://images.pexels.com/photos/2114365/pexels-photo-2114365.jpeg";
  const tabs = [["tonight", "Tonight"], ["events", "Events"], ["rewards", "Rewards"], ["info", "Info"]];

  return (
    <div data-testid="venue-profile" className="mx-auto min-h-screen max-w-[430px] bg-[#050505] pb-12 text-white">
      <div className="relative h-64">
        <img src={mediaUrl(venueImage)} alt={venue.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-black/30" />
        <button data-testid="venue-back-btn" onClick={() => navigate(-1)} className="glass absolute left-4 rounded-full p-2.5"
          style={{ top: "calc(1rem + env(safe-area-inset-top))" }}>
          <ArrowLeft size={18} />
        </button>
        <button data-testid="venue-fav-btn" onClick={toggleFav} className="glass absolute right-4 rounded-full p-2.5"
          style={{ top: "calc(1rem + env(safe-area-inset-top))" }}>
          <HeartStraight size={18} weight={favorited ? "fill" : "regular"} className={favorited ? "text-neon-gold" : "text-white"} />
        </button>
        <div className="absolute bottom-4 left-5 right-5">
          <PulseBadge pulse={venue.pulse} label={venue.pulse_label} />
          <div className="mt-2 flex items-center gap-2">
            <h1 className="font-display text-3xl font-black">{venue.name}</h1>
            {venue.founder && (
              <span data-testid="venue-founder-chip"
                className="inline-flex items-center gap-1 rounded-full border border-neon-gold/60 bg-neon-gold/15 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-neon-gold">
                <Crown size={11} weight="fill" /> Founding Venue
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-300">
            <span className="flex items-center gap-1"><MapPin size={12} />{venue.address}</span>
            <a data-testid="venue-directions-btn" href={directionsUrl(venue)} target="_blank" rel="noreferrer"
              onClick={() => trackAnalytics("directions_click", venue.id)}
              className="flex items-center gap-1 rounded-full border border-neon-violet/40 px-2.5 py-1 font-bold text-neon-violet">
              <NavigationArrow size={11} weight="fill" /> Directions
            </a>
            <span>Open til {venue.open_until}</span>
          </div>
        </div>
      </div>

      <div className="px-5">
        <div className="mt-4 flex flex-wrap gap-1.5">
          {music.map((m) => <span key={m} className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-zinc-300">{m}</span>)}
          <span className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-zinc-300">{venue.age_req}</span>
          <span className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-zinc-300">{venue.dress_code}</span>
          <span className="rounded-full bg-neon-gold/10 px-2.5 py-1 text-[11px] font-bold text-neon-gold">
            {venue.cover > 0 ? `Cover tonight: $${venue.cover}` : "No cover"}
          </span>
          {venue.cover_note && <span className="rounded-full bg-neon-green/10 px-2.5 py-1 text-[11px] font-bold text-neon-green">{venue.cover_note}</span>}
        </div>

        <div className="mt-6 flex gap-4 border-b border-white/10" data-testid="venue-tabs">
          {tabs.map(([key, label]) => (
            <button key={key} data-testid={`venue-tab-${key}`} onClick={() => setTab(key)}
              className={`pb-3 text-xs font-bold uppercase tracking-widest transition-colors duration-200 ${tab === key ? "border-b-2 border-neon-gold text-white" : "text-zinc-500"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "tonight" && (
          <div className="py-5">
            {tonightEvent ? (
              <div className="card-neon overflow-hidden" data-testid="tonight-event">
                <img src={mediaUrl(tonightEvent.image || venueImage)} alt="" className="h-36 w-full object-cover" />
                {(tonightEvent.media || []).filter((m) => m.content_type?.startsWith("video")).map((m) => (
                  <video key={m.url} src={mediaUrl(m.url)} controls playsInline className="w-full" data-testid="event-video" />
                ))}
                <div className="p-4">
                  <div className="neon-overline">TONIGHT · {tonightEvent.start}{tonightEvent.end ? `–${tonightEvent.end}` : ""}</div>
                  <div className="mt-1 font-display text-xl font-black">{tonightEvent.title}</div>
                  {(tonightEvent.address || venue.address) && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-zinc-400">
                      <MapPin size={12} /> {tonightEvent.address || venue.address}
                    </div>
                  )}
                  {tonightEvent.specials && (
                    <div className="mt-2 flex items-center gap-1.5 rounded-xl border border-neon-gold/30 bg-neon-gold/5 px-3 py-2 text-[11px] font-bold text-neon-gold" data-testid="event-specials">
                      <Martini size={13} /> {tonightEvent.specials}
                    </div>
                  )}
                  <p className="mt-1 text-xs text-zinc-400">{tonightEvent.description}</p>
                  <div className="mt-3 flex items-center gap-3 text-xs font-bold">
                    <span className="flex items-center gap-1 text-neon-gold"><Fire size={13} weight="fill" /> {tonightEvent.going_count} Going</span>
                    {tonightEvent.friends_going > 0 && <span className="flex items-center gap-1 text-neon-violet"><UsersThree size={13} /> {tonightEvent.friends_going} Friends</span>}
                    {tonightEvent.promoter_id && <span className="text-zinc-500">Guest list open</span>}
                  </div>
                  <div className="mt-3 rounded-xl border border-neon-yellow/30 bg-neon-yellow/5 px-3 py-2 text-[11px] font-bold text-neon-yellow">
                    NITEPASS REWARD: Check in → +250 NITE POINTS{venue.rewards_program?.[0] ? ` · ${venue.rewards_program[0].reward}` : ""}
                  </div>
                  {venue.entry_prepaid ? (
                    <div className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-neon-green/40 bg-neon-green/10 px-3 py-2.5 text-xs font-black text-neon-green" data-testid="entry-paid-chip">
                      <CheckCircle size={15} weight="fill" /> ENTRY PAID — show your NITEPASS at the door
                    </div>
                  ) : (tonightEvent.cover > 0 || venue.cover > 0) && (
                    <button data-testid="pay-entry-btn" onClick={payEntry} disabled={paying}
                      className="btn-neon mt-3 w-full !py-2.5 text-sm disabled:opacity-50">
                      {paying ? "Opening checkout..." : `Pay Entry — $${tonightEvent.cover || venue.cover}`}
                    </button>
                  )}
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button data-testid="going-btn" onClick={() => toggleGoing(tonightEvent.id)}
                      className={`rounded-full border py-2.5 text-xs font-bold transition-colors duration-200 ${goingIds.has(tonightEvent.id) ? "border-neon-violet bg-neon-violet/15 text-neon-violet" : "border-white/15 text-zinc-300 hover:border-neon-violet/50"}`}>
                      {goingIds.has(tonightEvent.id) ? "You're Going" : "I'm Going"}
                    </button>
                    {onListIds.has(tonightEvent.id) ? (
                      <div data-testid="on-guestlist-chip"
                        className="flex items-center justify-center rounded-full border border-neon-green/40 bg-neon-green/10 py-2.5 text-xs font-bold text-neon-green">
                        On the List
                      </div>
                    ) : (
                      <div data-testid="guestlist-invite-only"
                        className="flex items-center justify-center rounded-full border border-white/10 bg-white/5 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                        Guest List · Invite Only
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-zinc-500">No event listed tonight — but the doors are open.</p>
            )}
            {tonightEvent && <EventChat eventId={tonightEvent.id} />}
            {venue.drops?.length > 0 && (
              <div className="mt-4 rounded-2xl border border-neon-green/40 bg-[#07120b] p-4" data-testid="venue-drop">
                <div className="neon-overline !text-neon-green">NITE DROP LIVE</div>
                <div className="mt-1 font-display text-sm font-bold">{venue.drops[0].perk}</div>
                <div className="mt-1 text-xs text-zinc-400">First {venue.drops[0].total} check-ins · {Math.max(0, venue.drops[0].total - venue.drops[0].claimed)} left</div>
              </div>
            )}
          </div>
        )}

        {tab === "events" && (
          <div className="space-y-3 py-5" data-testid="venue-events-list">
            {events.map((e) => (
              <div key={e.id} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <img src={mediaUrl(e.image || venueImage)} alt="" className="h-14 w-14 rounded-xl object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold">{e.title}</div>
                  <div className="text-[11px] text-zinc-500">{e.date_label} · {e.start}{e.end ? `–${e.end}` : ""} · {e.cover > 0 ? `$${e.cover}` : "Free"}</div>
                  {(e.address || e.specials) && (
                    <div className="mt-0.5 truncate text-[10px] text-zinc-600">{e.address || venue.address}{e.specials ? ` · ${e.specials}` : ""}</div>
                  )}
                </div>
                <span className="flex items-center gap-1 text-[11px] font-bold text-neon-gold"><Fire size={12} weight="fill" />{e.going_count}</span>
              </div>
            ))}
            {events.length === 0 && <p className="py-6 text-center text-sm text-zinc-500">No upcoming events yet.</p>}
          </div>
        )}

        {tab === "rewards" && (
          <div className="space-y-2 py-5" data-testid="venue-rewards-list">
            {rewardsProgram.map((m) => (
              <div key={m.visits} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <span className="text-xs font-bold text-zinc-400">{m.visits} {m.visits === 1 ? "VISIT" : "VISITS"}</span>
                <span className="text-sm font-bold">{m.reward}</span>
              </div>
            ))}
            <p className="pt-2 text-[11px] text-zinc-500">Verified by QR scan at an authorized entry point. No scan, no reward.</p>
            {(venue.accepted_rewards || []).length > 0 && (
              <div className="pt-3" data-testid="venue-accepted-rewards">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">NITEPASS rewards accepted here</div>
                <div className="flex flex-wrap gap-1.5">
                  {venue.accepted_rewards.map((r) => (
                    <span key={r.id} data-testid={`accepted-reward-${r.id}`}
                      className="rounded-full border border-neon-gold/40 bg-neon-gold/10 px-3 py-1 text-[11px] font-bold text-neon-gold">
                      {r.title} · {r.point_cost} pts
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "info" && (
          <div className="space-y-3 py-5 text-sm" data-testid="venue-info">
            {[["Dress code", venue.dress_code], ["Age", venue.age_req], ["Address", venue.address], ["Open until", venue.open_until]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-white/5 pb-2">
                <span className="text-zinc-500">{k}</span><span className="font-semibold">{v}</span>
              </div>
            ))}
            <div className="pt-2">
              <div className="mb-2 text-xs font-bold uppercase tracking-widest text-zinc-500">Promoter team</div>
              {promoterTeam.length === 0 && <p className="text-xs text-zinc-600">No promoters listed.</p>}
              <div className="flex flex-wrap gap-2">
                {promoterTeam.map((p) => (
                  <button key={p.handle} data-testid={`team-promoter-${p.handle}`} onClick={() => navigate(`/${p.handle}`)}
                    className="rounded-full border border-neon-violet/30 bg-neon-violet/5 px-3 py-1 text-xs font-bold text-neon-violet transition-colors duration-200 hover:border-neon-violet hover:bg-neon-violet/15">
                    {p.handle} · {p.title || "Party Promoter"} {p.score ? `· ${p.score}` : ""}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2">
              {images.map((img, i) => (
                <img key={i} src={mediaUrl(img)} alt="" className="h-24 w-full rounded-xl object-cover" data-testid={`venue-gallery-img-${i}`} />
              ))}
            </div>
            {videos.length > 0 && (
              <div className="grid grid-cols-2 gap-2 pt-2" data-testid="venue-videos-grid">
                {videos.map((vid, i) => (
                  <video key={i} src={mediaUrl(vid)} controls preload="metadata" className="h-32 w-full rounded-xl object-cover" data-testid={`venue-video-${i}`} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
